(() => {
  'use strict';

  const RAPID_SELECTORS = [
    '#rapid-search-results',
    '.rps_container',
    '.rps-csb-container',
    '[class*="rps_predictive"]'
  ];

  function removeRapidSearchUI(root = document) {
    RAPID_SELECTORS.forEach((selector) => {
      root.querySelectorAll?.(selector).forEach((element) => {
        /*
         * Solo eliminamos nodos propios de Rapid.
         * No tocamos <predictive-search> ni los resultados nativos de Athens.
         */
        if (
          element.id === 'rapid-search-results' ||
          element.classList.contains('rps_container') ||
          element.classList.contains('rps-csb-container') ||
          [...element.classList].some((className) =>
            className.startsWith('rps_')
          )
        ) {
          element.remove();
        }
      });
    });
  }

  /*
   * SOLO se llama cuando ya se confirmó evidencia de Rapid en el mismo
   * ciclo (overlay insertado o clase rps_ detectada en un hijo de
   * <body>) -- nunca de forma incondicional. Athens/el theme también usan
   * overflow:hidden (y a veces height) inline en <html>/<body> para
   * bloquear el scroll mientras el menú mobile, el drawer del carrito o
   * un modal están abiertos; limpiar esas propiedades sin evidencia de
   * Rapid rompería ese bloqueo legítimo.
   */
  function cleanRapidInlineStyles() {
    document.documentElement.style.removeProperty('overflow');
    document.documentElement.style.removeProperty('height');
    document.body?.style.removeProperty('overflow');
    document.body?.style.removeProperty('height');
  }

  const RPS_CLASS_PREFIX = 'rps_';

  /*
   * Falla "buscador desaparece al escribir" (24SEP2026): Rapid, además
   * de insertar su propio overlay (#rapid-search-results / .rps_container
   * / .rps-csb-container, ya cubiertos arriba), ahora también agrega una
   * clase con prefijo "rps_" (observada: rps_body_child_visibility) a
   * CADA hijo directo de <body> -- header, main, footer, etc. -- para
   * ocultarlos mientras su buscador está "abierto". Como su overlay ya
   * queda oculto por el CSS de este mismo fix, el resultado neto es una
   * página completamente en blanco: todo <body> queda oculto y no hay
   * overlay visible que lo reemplace.
   *
   * Se retira cualquier clase que empiece con "rps_" de los hijos
   * DIRECTOS de <body> -- nunca se toca ninguna otra clase propia del
   * theme, y nunca se tocan nietos/descendientes más profundos. Esto no
   * depende de conocer el nombre exacto de la clase (puede cambiar en
   * una futura versión de la app): cualquier clase rps_* que aparezca
   * ahí se retira igual. Devuelve true solo si encontró y retiró algo
   * (evidencia real de Rapid en este ciclo), nunca un booleano optimista.
   */
  function stripRapidBodyChildClasses() {
    if (!document.body) return false;
    let removedAny = false;

    Array.prototype.forEach.call(document.body.children, (child) => {
      Array.prototype.slice.call(child.classList)
        .filter((className) => className.indexOf(RPS_CLASS_PREFIX) === 0)
        .forEach((className) => {
          child.classList.remove(className);
          removedAny = true;
        });
    });

    return removedAny;
  }

  /*
   * Puntos 1 y 2 de la revisión (24SEP2026):
   *
   * 1) El observer de ATRIBUTOS ya no usa subtree:true sobre todo el
   *    documento. Se observa <html> y <body> (solo su propio atributo
   *    style) de forma individual, y cada hijo DIRECTO de <body> (solo su
   *    propio atributo class) de forma individual -- nunca sus
   *    descendientes. watchBodyChildren() vuelve a registrar hijos nuevos
   *    cuando el observer de childList (que sí necesita subtree:true,
   *    para encontrar el overlay de Rapid en cualquier profundidad)
   *    detecta que se agregó algo directamente a <body>. Autolimitado:
   *    stripRapidBodyChildClasses() solo escribe cuando encuentra una
   *    clase rps_ real -- si no encuentra ninguna (por ejemplo, en la
   *    segunda pasada, justo después de haberla retirado), no produce
   *    ninguna mutación nueva y el ciclo se apaga solo.
   *
   * 2) cleanRapidInlineStyles() deja de llamarse en cada cambio de style
   *    de <html>/<body>. Ahora exige evidencia de Rapid EN EL MISMO lote
   *    de mutaciones (overlay insertado o clase rps_ retirada de un hijo
   *    de <body>) antes de tocar overflow/height -- así nunca interfiere
   *    con el bloqueo de scroll del menú mobile, el drawer del carrito o
   *    los modales propios del theme.
   */
  function disableRapidSearch() {
    removeRapidSearchUI();
    stripRapidBodyChildClasses();
    cleanRapidInlineStyles();

    // Dos instancias de MutationObserver a propósito, no una: llamar
    // observe() dos veces sobre el MISMO nodo con la MISMA instancia no
    // combina las opciones, las REEMPLAZA (comportamiento documentado del
    // spec) -- si childListObserver y attributeObserver compartieran
    // instancia, la segunda llamada sobre document.documentElement habría
    // pisado silenciosamente el {childList:true, subtree:true} de la
    // primera. Con dos instancias independientes, cada una mantiene su
    // propio registro por nodo sin pisarse.

    // Observer 1: SOLO childList + subtree:true sobre documentElement --
    // imprescindible para encontrar el overlay de Rapid sin importar en
    // qué profundidad lo inserte. No mira atributos.
    const childListObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;

          if (
            node.id === 'rapid-search-results' ||
            node.matches?.('.rps_container, .rps-csb-container') ||
            node.querySelector?.('#rapid-search-results, .rps_container, .rps-csb-container')
          ) {
            removeRapidSearchUI(node.parentElement || document);
          }
        }

        if (mutation.target === document.body) {
          watchBodyChildren();
        }
      }
    });

    childListObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    // Observer 2: SOLO atributos, y SOLO sobre nodos puntuales -- nunca
    // subtree:true. style de <html>/<body> cada uno por separado, class
    // de cada hijo DIRECTO de <body> cada uno por separado. Nunca ve
    // cambios de atributo en ningún otro lugar del documento.
    const attributeObserver = new MutationObserver((mutations) => {
      let rapidEvidenceThisBatch = false;

      for (const mutation of mutations) {
        if (mutation.attributeName === 'class' && mutation.target.parentElement === document.body) {
          if (stripRapidBodyChildClasses()) rapidEvidenceThisBatch = true;
        }
        // mutation.attributeName === 'style' (html o body): no dispara
        // nada por sí sola -- ver cleanRapidInlineStyles() más abajo,
        // solo se llama cuando ESTE MISMO lote también trae evidencia de
        // Rapid (una clase rps_ retirada arriba).
      }

      if (rapidEvidenceThisBatch) cleanRapidInlineStyles();
    });

    attributeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    if (document.body) {
      attributeObserver.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    }

    // Nodos de body ya registrados en attributeObserver -- evita volver a
    // llamar observe() sobre uno que ya está vigilado (no es incorrecto
    // per spec, observe() dos veces con las mismas opciones sobre el
    // mismo nodo simplemente reemplaza por opciones idénticas, pero no
    // hay ninguna razón para repetirlo en cada mutación de childList).
    const observedBodyChildren = new WeakSet();

    function watchBodyChildren() {
      if (!document.body) return;
      let foundNewChild = false;

      Array.prototype.forEach.call(document.body.children, (child) => {
        if (observedBodyChildren.has(child)) return;
        observedBodyChildren.add(child);
        attributeObserver.observe(child, { attributes: true, attributeFilter: ['class'] });
        foundNewChild = true;
      });

      // Un hijo nuevo puede llegar con la clase rps_ YA puesta desde su
      // creación (antes de insertarse en <body>) -- ese caso nunca
      // dispara una mutación de atributo "class" (esa solo existe para
      // cambios POSTERIORES a la inserción), así que quedaría sin
      // limpiar hasta el próximo cambio de clase, si es que llega.
      // Se revisa de inmediato en cuanto se detecta el nodo nuevo,
      // igual que ya se hace al cargar la página (disableRapidSearch()
      // más abajo llama stripRapidBodyChildClasses() una vez al
      // inicio por el mismo motivo).
      if (foundNewChild && stripRapidBodyChildClasses()) {
        cleanRapidInlineStyles();
      }
    }

    watchBodyChildren();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', disableRapidSearch, {
      once: true
    });
  } else {
    disableRapidSearch();
  }
})();